import { useMemo } from "react";
import { noop } from "@common/helpers/function";
import { BillView, BillViewSchema } from "@common/types/views/billViews";
import { BillsViewContainer } from "@components/bills/views/billsView/BillsViewContainer";
import { BillsTaskTableProvider } from "@components/tasks/TaskBody/Bills/BillsTaskTableProvider";
import { buildBillsTaskQuery } from "@components/tasks/TaskBody/Bills/billsTaskFilters";
import { useBillsTaskViewConfig } from "@components/tasks/TaskBody/Bills/useBillsTaskViewConfig";
import {
    TemplateTaskDataTable,
    TemplateTaskTableProps,
} from "./TemplateTaskDataTable";
import { ResolvedTaskView } from "./useResolvedTaskView";

interface Props {
    templateView: BillView;
    onTemplateViewChange: (view: BillView) => void;
    resolvedView: ResolvedTaskView;
    workspaceId: string | null;
}

const ResolvedBillsTable = ({
    view,
    onViewChange,
    emptyView,
}: TemplateTaskTableProps<BillView>) => {
    const { hasAnyAccrualLedgers } = useBillsTaskViewConfig();
    const filters = useMemo(
        () => buildBillsTaskQuery({ viewFilters: view.filters }),
        [view.filters],
    );

    return (
        <BillsTaskTableProvider filters={filters}>
            <BillsViewContainer
                view={view}
                onBillClick={noop}
                hasAnyAccrualLedgers={hasAnyAccrualLedgers}
                hasConnectedBillAccounts
                onClearFilters={noop}
                emptyView={emptyView}
                onViewChange={onViewChange}
            />
        </BillsTaskTableProvider>
    );
};

const renderBillsTable = (props: TemplateTaskTableProps<BillView>) => (
    <ResolvedBillsTable {...props} />
);

/**
 * The bills a template task would show for the previewed client. Read-only: no
 * selection, bulk actions or row details.
 */
export const TemplateBillsDataTable = (props: Props) => (
    <TemplateTaskDataTable
        {...props}
        viewSchema={BillViewSchema}
        rowsLabel="bills"
        renderTable={renderBillsTable}
    />
);
